import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import { HeatingCircuit } from 'z3k-config';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { useHeatingCircuitTargetThermometer, useZ3KDeviceState, useZ3KObjectState, } from 'console/shared/hooks/device/z3k';
import { getControlTempRange } from 'console/shared/components/dialogs/heating-circuit-widget/utils';
import { getConsumerControlState } from 'console/shared/content/device/heating-circuits/utils';
import { getZ3KControlState, getZ3KHeatingCircuitThermometers, } from 'console/shared/content/device/heating-circuits/z3k/utils';
import { selectFailInBoilers } from 'console/baxi/widgets/heating-circuits/model/selectors';
import { useAppSelector } from 'shared/hooks/redux/hooks';
import { selectTurnOnExtPanel } from '../../../boilers-heating-circuits/thumblers/z3k/selectors';
import { useHeatingDeviceEntitiesConfig } from 'console/shared/contexts/heating-device/types';
import { useHeatingCircuitControlValues, useHeatingCircuitMode } from '../model/hooks';
import { HeatingCircuitContent } from './types';
import { HeatingCircuitControlProvider } from './control';
import { formatTemp } from 'utils';
import { HeatingMode } from 'z3k-parser/model/z3k-config/20-heating-mode';
export const Z3KHeatingCircuitProvider = memo(function Z3KHeatingCircuitProvider(props) {
    const { children, config } = props;
    const { id: any_id, is_dhw, z3k_circuit: circuit, control_mode_config, thermometers_config, onTargetTemperatureChange, } = config;
    const z3k_circuit = circuit || {};
    const id = any_id;
    const { z3k_config } = useDeviceConfig();
    const z3k_state = useZ3KDeviceState();
    const z3k_heating_circuit_state = useZ3KObjectState(id);
    const control_state = useMemo(() => getZ3KControlState(z3k_circuit, z3k_heating_circuit_state), [z3k_circuit, z3k_heating_circuit_state]);
    const { target_sensor_id, target_temp: target_temperature, mode_id, setpoint_temp: setpoint_temperature, } = z3k_heating_circuit_state;
    const target_thermometer = useHeatingCircuitTargetThermometer(z3k_config, is_dhw && (target_sensor_id == null || target_sensor_id == 0)
        ? thermometers_config.dhw?.main.id ?? null
        : target_sensor_id, is_dhw);
    const is_manual_mode = useMemo(() => mode_id == null || mode_id == 0, [mode_id]);
    const { min_temperature: default_min_temperature, max_temperature: default_max_temperature, } = useMemo(() => getControlTempRange(z3k_circuit, z3k_heating_circuit_state, z3k_config), [z3k_circuit, z3k_heating_circuit_state, z3k_config]);
    const is_working = useMemo(() => HeatingCircuit.isWorking(z3k_heating_circuit_state), [z3k_heating_circuit_state]);
    const working_mode = useMemo(() => {
        const heat_request = is_working && !HeatingCircuit.isCooling(z3k_circuit);
        const cooling = is_working && HeatingCircuit.isCooling(z3k_circuit);
        return { heat_request, cooling };
    }, [z3k_circuit, is_working]);
    const z3k_heating_mode = useMemo(() => HeatingMode.get.one(z3k_config, (heating_mode) => mode_id === heating_mode.id), [mode_id, z3k_config]);
    const heating_zone = useMemo(() => z3k_heating_mode?.heating_zones.find(({ heating_circuit }) => heating_circuit === id), [z3k_heating_mode?.heating_zones]);
    const is_fail = useMemo(() => HeatingCircuit.isFail(z3k_heating_circuit_state), [z3k_heating_circuit_state]);
    const thermometers = useMemo(() => getZ3KHeatingCircuitThermometers(thermometers_config, z3k_state, is_dhw), [thermometers_config, z3k_state, is_dhw]);
    const control_mode = useMemo(() => {
        const control_mode_state = getConsumerControlState(control_state);
        if (control_mode_state.mode == null) {
            return control_mode_config;
        }
        return control_mode_state;
    }, [z3k_circuit, control_mode_config]);
    const { name: thermometer_name, temperature: current_temperature } = target_thermometer;
    const is_off_mode = useMemo(() => (heating_zone != null ? HeatingMode.isOffHz(heating_zone) : false), [heating_zone]);
    const is_antileg_enabled = useMemo(() => HeatingCircuit.isAntilegEnabled(z3k_circuit), [z3k_circuit]);
    const is_antileg_turn_on = useMemo(() => HeatingCircuit.isAntilegTurnOn(z3k_circuit), [z3k_circuit]);
    const manage_by_ext_panel = useAppSelector(selectTurnOnExtPanel);
    const is_fail_in_boiler = useAppSelector(selectFailInBoilers);
    const { heating_modes: all_heating_modes } = useHeatingDeviceEntitiesConfig();
    const heating_modes = useMemo(() => all_heating_modes?.filter((heating_mode) => heating_mode.heating_zones?.some(({ heating_circuit }) => heating_circuit == id)), [all_heating_modes, id]);
    const { temperature_decimal_places, min_temperature, max_temperature } = useHeatingCircuitControlValues(default_min_temperature, default_max_temperature);
    const { summer_mode } = control_state;
    const { heating_mode, heating_mode_color } = useHeatingCircuitMode(mode_id, heating_modes, summer_mode);
    const state = useMemo(() => ({
        ...control_state,
        current_temperature,
        current_temperature_str: formatTemp(current_temperature),
        thermometer_name,
        heating_mode_color,
        manage_by_ext_panel,
        target_temperature,
        control_mode,
        mode_id,
        is_manual_mode,
        is_fail_in_boiler,
        min_temperature,
        is_off_mode,
        is_antileg_enabled,
        is_antileg_turn_on,
        max_temperature,
        working_mode,
        heating_zone,
        setpoint_temperature,
        is_fail,
        thermometers: {
            ...thermometers,
            target: target_thermometer,
        },
    }), [
        target_temperature,
        current_temperature,
        thermometer_name,
        mode_id,
        manage_by_ext_panel,
        is_manual_mode,
        heating_mode_color,
        min_temperature,
        max_temperature,
        is_fail_in_boiler,
        working_mode,
        is_off_mode,
        is_antileg_enabled,
        is_antileg_turn_on,
        heating_zone,
        setpoint_temperature,
        control_state,
        is_fail,
        target_thermometer,
        thermometers,
    ]);
    const heating_circuit = useMemo(() => ({
        ...{ heating_mode, heating_modes, temperature_decimal_places, ...config },
        ...state,
    }), [config, state, heating_mode, heating_modes, temperature_decimal_places]);
    return (_jsx(HeatingCircuitContent.Provider, { value: heating_circuit, children: _jsx(HeatingCircuitControlProvider, { onTargetTemperatureChange: onTargetTemperatureChange, heating_circuit: heating_circuit, children: children }) }));
});
